import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { date, dateTime, humanise, inr, mobile as fmtMobile, rupees } from '../lib/format';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardHead,
  Empty,
  Field,
  Modal,
  Notice,
  Select,
  StatusBadge,
  Textarea,
  useToast,
} from '../components/ui';
import { deviceLabel, duration } from './LoginHistory';
import type { LoginRow } from './LoginHistory';

interface UserDetail {
  id: string;
  mobile: string;
  displayName: string | null;
  email: string | null;
  profileImage: string | null;
  languages: string[];
  roles: string[];
  kycStatus: string;
  isBlocked: boolean;
  blockReason: string | null;
  blockedAt: string | null;
  lockedUntil: string | null;
  lockReason: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  homeLocation: { id: string; name: string; path: string } | null;
  kycRecords: Array<{
    id: string;
    docType: string;
    status: string;
    nameOnDoc: string | null;
    docLast4: string | null;
    rejectReason: string | null;
    verifiedAt: string | null;
    createdAt: string;
  }>;
  provider: {
    id: string;
    name: string;
    isLive: boolean;
    teamSize: number;
    activeJobs: number;
    ratingAvg: string;
    ratingCount: number;
    policeVerification: string;
    category: { name: string };
  } | null;
  agent: {
    id: string;
    referralCode: string;
    depth: number;
    enrolledAt: string;
    mobileVerifiedAt: string | null;
    whatsappMobile: string | null;
    address: string | null;
    aadhaarLast4: string | null;
    panMasked: string | null;
    bankHolderName: string | null;
    bankAccountLast4: string | null;
    bankIfsc: string | null;
    bankName: string | null;
  } | null;
  listings: Array<{ id: string; title: string; status: string; priceInr: string | null; createdAt: string }>;
  payments: Array<{ id: string; purpose: string; amountPaise: number; status: string; createdAt: string }>;
  subscriptions: Array<{ id: string; status: string; startsAt: string; endsAt: string; plan: { label: string } }>;
  devices: Array<{ id: string; platform: string; appVersion: string | null; createdAt: string }>;
  _count: { listings: number; bookings: number; notifications: number };
}

export function UserDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [blocking, setBlocking] = useState(false);
  const [reason, setReason] = useState('');
  const [roleToAdd, setRoleToAdd] = useState('');
  const [locking, setLocking] = useState(false);
  const [lockHours, setLockHours] = useState('24');
  const [lockReason, setLockReason] = useState('');
  const toast = useToast();
  const qc = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['user', id],
    queryFn: () => api.get<UserDetail>(`/admin/users/${id}`),
  });

  // Their recent sign-ins, and which of them are still signed in.
  const logins = useQuery({
    queryKey: ['login-records', 'user', id],
    queryFn: () => api.get<{ items: LoginRow[] }>(`/admin/login-records?userId=${id}&limit=10`),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['login-records'] });
    qc.invalidateQueries({ queryKey: ['user', id] });
    qc.invalidateQueries({ queryKey: ['users'] });
  };

  const block = useMutation({
    mutationFn: (body: { isBlocked: boolean; reason?: string }) => api.patch(`/admin/users/${id}/block`, body),
    onSuccess: (_d, v) => {
      toast(v.isBlocked ? 'User blocked and sessions revoked' : 'User unblocked', 'success');
      setBlocking(false);
      setReason('');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const forceLogout = useMutation({
    mutationFn: () => api.post<{ sessionsEnded: number }>(`/admin/users/${id}/force-logout`, {}),
    onSuccess: (r) => {
      toast(`Signed out of ${r.sessionsEnded} session${r.sessionsEnded === 1 ? '' : 's'}`, 'success');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const endSession = useMutation({
    mutationFn: (sessionId: string) => api.post(`/admin/sessions/${sessionId}/revoke`, {}),
    onSuccess: () => {
      toast('Session ended', 'success');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const lock = useMutation({
    mutationFn: (body: { hours: number; reason: string }) => api.post(`/admin/users/${id}/lock`, body),
    onSuccess: () => {
      toast('Account locked and signed out', 'success');
      setLocking(false);
      setLockReason('');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const unlock = useMutation({
    mutationFn: () => api.del(`/admin/users/${id}/lock`),
    onSuccess: () => {
      toast('Lock lifted', 'success');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const grantRole = useMutation({
    mutationFn: (role: string) => api.post(`/admin/users/${id}/roles`, { role }),
    onSuccess: () => {
      toast(
        roleToAdd === 'AGENT' ? 'Made a Global Axis Partner — a Partner ID was issued' : 'Role granted',
        'success',
      );
      setRoleToAdd('');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const revokeRole = useMutation({
    mutationFn: (role: string) => api.del(`/admin/users/${id}/roles/${role}`),
    onSuccess: () => {
      toast('Role revoked', 'success');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  if (isLoading) return <div className="skeleton" style={{ height: 320 }} />;
  if (isError) return <Empty icon="⚠️" title="Could not load this user" body={(error as Error).message} />;
  if (!data) return null;

  const grantable = ['SELLER', 'SERVICE_PROVIDER', 'AGENT', 'ADMIN'].filter((r) => !data.roles.includes(r));
  const locked = data.lockedUntil != null && new Date(data.lockedUntil) > new Date();
  const sessions = logins.data?.items ?? [];

  return (
    <div className="stack" style={{ gap: 'var(--s5)' }}>
      <Button onClick={() => navigate(-1)}>← Back</Button>

      {data.isBlocked && (
        <Notice tone="danger" title="This account is blocked">
          {data.blockReason ?? 'No reason was recorded.'} They cannot sign in, and any provider profile is hidden.
          {data.blockedAt ? ` Blocked ${dateTime(data.blockedAt)}.` : ''}
        </Notice>
      )}

      {locked && (
        <Notice tone="danger" title={`Locked until ${dateTime(data.lockedUntil)}`}>
          {data.lockReason ?? 'No reason was recorded.'} They cannot sign in or use the app until then. It lifts by
          itself.
        </Notice>
      )}

      <Card>
        <div className="row" style={{ alignItems: 'flex-start', gap: 'var(--s4)' }}>
          <Avatar name={data.displayName} src={data.profileImage} size="lg" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.3px' }}>
              {data.displayName ?? 'No name set'}
            </div>
            <div className="cell-muted" style={{ marginTop: 2 }}>
              {fmtMobile(data.mobile)}
              {data.email && ` · ${data.email}`}
            </div>
            <div className="row row--wrap" style={{ gap: 5, marginTop: 10 }}>
              {data.roles.map((r) => (
                <span key={r} className="row" style={{ gap: 3 }}>
                  <StatusBadge value={r} />
                  {r !== 'CUSTOMER' && (
                    <button
                      className="btn--link"
                      style={{ background: 'none', border: 'none', color: 'var(--ink-faint)', fontSize: 12 }}
                      title={`Revoke ${humanise(r)}`}
                      onClick={() => revokeRole.mutate(r)}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
              <StatusBadge value={data.kycStatus} />
            </div>
          </div>
          <div className="row">
            {grantable.length > 0 && (
              <>
                <Select
                  value={roleToAdd}
                  placeholder="Grant role…"
                  onChange={(e) => setRoleToAdd(e.target.value)}
                  // "Agent" is the Global Axis Partner. Granting it issues a
                  // Partner ID and a referral code, so it is named for what
                  // it does.
                  options={grantable.map((r) => ({
                    value: r,
                    label: r === 'AGENT' ? 'Global Axis Partner' : humanise(r),
                  }))}
                />
                <Button disabled={!roleToAdd} loading={grantRole.isPending} onClick={() => grantRole.mutate(roleToAdd)}>
                  Grant
                </Button>
              </>
            )}
            <Button loading={forceLogout.isPending} onClick={() => forceLogout.mutate()}>
              Sign out everywhere
            </Button>
            {locked ? (
              <Button variant="success" loading={unlock.isPending} onClick={() => unlock.mutate()}>
                Lift lock
              </Button>
            ) : (
              <Button onClick={() => setLocking(true)}>Lock…</Button>
            )}
            {data.isBlocked ? (
              <Button variant="success" loading={block.isPending} onClick={() => block.mutate({ isBlocked: false })}>
                Unblock
              </Button>
            ) : (
              <Button variant="danger" onClick={() => setBlocking(true)}>
                Block
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card flush>
        <CardHead
          title="Sign-ins"
          sub="The ten most recent, and which are still signed in"
          action={
            <Link to={`/login-history?search=${encodeURIComponent(data.mobile)}`}>
              <Button size="sm">Full history</Button>
            </Link>
          }
        />
        {sessions.length === 0 ? (
          <Empty icon="⏻" title="No sign-ins recorded" body="Sign-ins are recorded from the day this was switched on." />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Result</th>
                  <th>Device</th>
                  <th>IP</th>
                  <th>Session</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sessions.map((r) => (
                  <tr key={r.id}>
                    <td className="cell-muted">{dateTime(r.createdAt)}</td>
                    <td>
                      {r.status === 'FAILED' ? (
                        <Badge tone="red">Failed</Badge>
                      ) : r.isActive ? (
                        <Badge tone="green" dot>
                          Signed in
                        </Badge>
                      ) : (
                        <Badge tone="neutral">Ended</Badge>
                      )}
                    </td>
                    <td>{deviceLabel(r)}</td>
                    <td className="cell-mono">{r.ip ?? '—'}</td>
                    <td>{duration(r.durationSeconds)}</td>
                    <td>
                      {r.isActive && r.sessionId && (
                        <Button
                          size="sm"
                          variant="danger"
                          loading={endSession.isPending && endSession.variables === r.sessionId}
                          onClick={() => endSession.mutate(r.sessionId as string)}
                        >
                          End
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid grid--2">
        <Card>
          <CardHead title="Account" />
          <div className="kv" style={{ paddingTop: 'var(--s4)' }}>
            <span className="kv__k">Joined</span>
            <span className="kv__v">{dateTime(data.createdAt)}</span>
            <span className="kv__k">Last seen</span>
            <span className="kv__v">{data.lastLoginAt ? dateTime(data.lastLoginAt) : 'never signed in'}</span>
            <span className="kv__k">Home location</span>
            <span className="kv__v">{data.homeLocation?.name ?? '—'}</span>
            <span className="kv__k">Languages</span>
            <span className="kv__v">{data.languages.length ? data.languages.join(', ') : '—'}</span>
            <span className="kv__k">Devices</span>
            <span className="kv__v">
              {data.devices.length
                ? data.devices.map((d) => `${d.platform}${d.appVersion ? ` ${d.appVersion}` : ''}`).join(', ')
                : 'none registered'}
            </span>
            <span className="kv__k">Bookings</span>
            <span className="kv__v">{data._count.bookings}</span>
          </div>
        </Card>

        <Card>
          <CardHead title="KYC history" sub="No document images or full ID numbers are stored (§7.3)" />
          <div style={{ paddingTop: 'var(--s4)' }}>
            {data.kycRecords.length === 0 ? (
              <Empty icon="⛉" title="No verification attempts" />
            ) : (
              <div className="stack" style={{ gap: 10 }}>
                {data.kycRecords.map((k) => (
                  <div className="row" key={k.id} style={{ alignItems: 'flex-start' }}>
                    <StatusBadge value={k.status} />
                    <div style={{ flex: 1 }}>
                      <div className="cell-strong">
                        {humanise(k.docType)}
                        {k.docLast4 && <span className="cell-muted"> · ends {k.docLast4}</span>}
                      </div>
                      <div className="cell-muted">
                        {k.nameOnDoc ?? 'no name returned'} · {date(k.createdAt)}
                      </div>
                      {k.rejectReason && <div style={{ color: 'var(--danger)', fontSize: 12.5 }}>{k.rejectReason}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {(data.provider || data.agent) && (
        <div className="grid grid--2">
          {data.provider && (
            <Card>
              <CardHead
                title="Service provider"
                action={<Badge tone={data.provider.isLive ? 'green' : 'neutral'}>{data.provider.isLive ? 'Live' : 'Offline'}</Badge>}
              />
              <div className="kv" style={{ paddingTop: 'var(--s4)' }}>
                <span className="kv__k">Business</span>
                <span className="kv__v">{data.provider.name}</span>
                <span className="kv__k">Category</span>
                <span className="kv__v">{data.provider.category.name}</span>
                <span className="kv__k">Capacity</span>
                <span className="kv__v">
                  {data.provider.activeJobs} of {data.provider.teamSize} in use
                </span>
                <span className="kv__k">Rating</span>
                <span className="kv__v">
                  {Number(data.provider.ratingAvg).toFixed(2)} from {data.provider.ratingCount}
                </span>
                <span className="kv__k">Police verification</span>
                <span className="kv__v">
                  <StatusBadge value={data.provider.policeVerification} />
                </span>
              </div>
            </Card>
          )}

          {data.agent && (
            <Card>
              <CardHead title="Partner" />
              <div className="kv" style={{ paddingTop: 'var(--s4)' }}>
                <span className="kv__k">Referral code</span>
                <span className="kv__v cell-mono">{data.agent.referralCode}</span>
                <span className="kv__k">Depth</span>
                <span className="kv__v">{data.agent.depth}</span>
                <span className="kv__k">Enrolled</span>
                <span className="kv__v">{date(data.agent.enrolledAt)}</span>
                <span className="kv__k">Mobile</span>
                <span className="kv__v">{data.agent.mobileVerifiedAt ? 'Verified by code' : 'Not verified at enrolment'}</span>
                {data.agent.whatsappMobile && (
                  <>
                    <span className="kv__k">WhatsApp</span>
                    <span className="kv__v cell-mono">{data.agent.whatsappMobile}</span>
                  </>
                )}
                {data.agent.address && (
                  <>
                    <span className="kv__k">Address</span>
                    <span className="kv__v">{data.agent.address}</span>
                  </>
                )}
                <span className="kv__k">Aadhaar</span>
                <span className="kv__v cell-mono">{data.agent.aadhaarLast4 ? `XXXX XXXX ${data.agent.aadhaarLast4}` : '—'}</span>
                <span className="kv__k">PAN</span>
                <span className="kv__v cell-mono">{data.agent.panMasked ?? '—'}</span>
                <span className="kv__k">Bank</span>
                <span className="kv__v">
                  {data.agent.bankAccountLast4
                    ? `${data.agent.bankHolderName} · ${data.agent.bankName} · A/c XXXX${data.agent.bankAccountLast4} · ${data.agent.bankIfsc}`
                    : '—'}
                </span>
                <span className="kv__k">Ledger</span>
                <span className="kv__v">
                  <Link to={`/bonuses`}>View bonuses →</Link>
                </span>
              </div>
            </Card>
          )}
        </div>
      )}

      <div className="grid grid--2">
        <Card flush>
          <CardHead title="Recent listings" sub={`${data._count.listings} in total`} />
          {data.listings.length === 0 ? (
            <Empty icon="▤" title="No listings" />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <tbody>
                  {data.listings.map((l) => (
                    <tr key={l.id}>
                      <td className="cell-strong cell-clip">{l.title}</td>
                      <td>
                        <StatusBadge value={l.status} />
                      </td>
                      <td className="cell-num">{rupees(l.priceInr)}</td>
                      <td className="cell-muted">{date(l.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card flush>
          <CardHead title="Recent payments" />
          {data.payments.length === 0 ? (
            <Empty icon="₹" title="No payments" />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <tbody>
                  {data.payments.map((p) => (
                    <tr key={p.id}>
                      <td>{humanise(p.purpose)}</td>
                      <td>
                        <StatusBadge value={p.status} />
                      </td>
                      <td className="cell-num">{inr(p.amountPaise)}</td>
                      <td className="cell-muted">{date(p.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {data.subscriptions.length > 0 && (
        <Card flush>
          <CardHead title="Subscriptions" />
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>From</th>
                  <th>Until</th>
                </tr>
              </thead>
              <tbody>
                {data.subscriptions.map((s) => (
                  <tr key={s.id}>
                    <td className="cell-strong">{s.plan.label}</td>
                    <td>
                      <StatusBadge value={s.status} />
                    </td>
                    <td className="cell-muted">{date(s.startsAt)}</td>
                    <td className="cell-muted">{date(s.endsAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {locking && (
        <Modal
          title="Lock this account for a while"
          sub="They are signed out now and cannot sign in until the lock passes. Unlike a block, it lifts by itself."
          onClose={() => setLocking(false)}
          footer={
            <>
              <Button onClick={() => setLocking(false)}>Cancel</Button>
              <Button
                variant="danger"
                loading={lock.isPending}
                disabled={lockReason.trim().length < 3 || !(Number(lockHours) >= 1)}
                onClick={() => lock.mutate({ hours: Math.round(Number(lockHours)), reason: lockReason.trim() })}
              >
                Lock account
              </Button>
            </>
          }
        >
          <div className="stack">
            <Field label="For how long">
              <Select
                value={lockHours}
                onChange={(e) => setLockHours(e.target.value)}
                options={[
                  { value: '1', label: '1 hour' },
                  { value: '24', label: '24 hours' },
                  { value: '72', label: '3 days' },
                  { value: '168', label: '7 days' },
                  { value: '720', label: '30 days' },
                ]}
              />
            </Field>
            <Field label="Reason" hint="Recorded in the audit trail.">
              <Textarea value={lockReason} autoFocus onChange={(e) => setLockReason(e.target.value)} />
            </Field>
          </div>
        </Modal>
      )}

      {blocking && (
        <Modal
          title="Block this account"
          sub="They will be signed out immediately, and any provider profile is taken offline."
          onClose={() => setBlocking(false)}
          footer={
            <>
              <Button onClick={() => setBlocking(false)}>Cancel</Button>
              <Button
                variant="danger"
                loading={block.isPending}
                disabled={!reason.trim()}
                onClick={() => block.mutate({ isBlocked: true, reason: reason.trim() })}
              >
                Block account
              </Button>
            </>
          }
        >
          <Field label="Reason" hint="Recorded in the audit trail.">
            <Textarea value={reason} autoFocus onChange={(e) => setReason(e.target.value)} />
          </Field>
        </Modal>
      )}
    </div>
  );
}
